import { Skeleton } from '@/components/ui/skeleton'

// Table Skeleton Component
export function TableSkeleton({
  rows = 5,
  columns = 6,
  filterSkeleton = true,
}: Readonly<{
  rows?: number
  columns?: number
  filterSkeleton?: boolean
}>) {
  return (
    <div
      className="w-full flex flex-col h-full"
      aria-label="Loading table data"
      aria-busy="true"
    >
      {/* Table Header Section Skeleton */}
      {filterSkeleton && (
        <div className="flex items-center justify-between px-4 lg:px-6 py-4 bg-transparent sticky top-0 z-10 flex-wrap gap-2">
          <div>
            <Skeleton className="h-7 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-24" />
          </div>
        </div>
      )}

      {/* Data Table Skeleton */}
      <div className="flex-1 mx-4 lg:mx-6 rounded-xl border border-ss-neutral-50 bg-ss-neutral-white overflow-hidden flex flex-col mb-4 lg-mb-6">
        <div className="flex-1 overflow-auto">
          {/* Table Header Skeleton */}
          <div className="bg-ss-neutral-25 sticky top-0 z-10 border-b border-ss-neutral-50">
            <div className="flex h-12 px-4 items-center">
              {Array.from({ length: columns }).map((_, index) => (
                <div key={index} className="flex-1 px-2" aria-hidden="true">
                  <Skeleton className="h-4 w-full max-w-24" />
                </div>
              ))}
            </div>
          </div>

          {/* Table Body Skeleton */}
          <div>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <div
                key={rowIndex}
                className="flex h-16 px-4 items-center border-b border-ss-neutral-50"
              >
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <div
                    key={colIndex}
                    className="flex-1 px-2"
                    aria-hidden="true"
                  >
                    {colIndex === 0 ? (
                      // First column - typically has checkbox and profile
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </div>
                    ) : colIndex === columns - 1 ? (
                      // Last column - typically actions
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                      </div>
                    ) : (
                      // Other columns - regular content
                      <Skeleton className="h-4 w-full max-w-20" />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Skeleton */}
        <div
          className="flex items-center justify-between p-4 border-t border-ss-neutral-50 bg-ss-neutral-white sticky bottom-0 z-10 flex-wrap gap-2"
          aria-hidden="true"
        >
          <Skeleton className="h-5 w-24" />
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8" />
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-8 w-8" />
              ))}
              <Skeleton className="h-8 w-8" />
            </div>
          </div>
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
    </div>
  )
}

// Section Cards Skeleton Component
export function SectionCardsSkeleton({
  cardCount = 4,
}: Readonly<{
  cardCount?: number
}>) {
  return (
    <div>
      <div>
        <Skeleton className="h-6 w-32 mb-4 lg:mb-6 px-4 lg:px-6 mx-7" />
      </div>
      <div
        className="grid grid-cols-1 gap-4 px-4 lg:px-6 md:grid-cols-2 xl:grid-cols-4"
        aria-label="Loading summary cards"
        aria-busy="true"
      >
        {Array.from({ length: cardCount }).map((_, index) => (
          <div
            key={index}
            className="relative w-full min-w-0 h-[110px] rounded-xl border border-ss-neutral-50 bg-ss-neutral-white overflow-hidden p-2.5 pb-0"
            aria-hidden="true"
          >
            <div className="rounded-t-xl rounded-tr-2xl border border-white/40 backdrop-blur-sm bg-white/40">
              <div className="flex flex-col gap-2.5 h-full p-1.5 pr-4">
                {/* Header with Icon and Title Skeleton */}
                <div className="flex items-center gap-3 flex-1">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <Skeleton className="h-5 w-24" />
                </div>

                {/* Value and Growth Skeleton */}
                <div className="flex items-center justify-between">
                  <Skeleton className="h-8 w-16" />
                  <div className="flex items-center gap-1">
                    <Skeleton className="h-4 w-12" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Filter Bar Skeleton Component
export function FilterBarSkeleton() {
  return (
    <div
      className="flex items-center gap-2 px-4 lg:px-6 py-2 flex-wrap"
      aria-label="Loading filter bar"
      aria-busy="true"
    >
      {Array.from({ length: 3 }).map((_, index) => (
        <Skeleton
          key={index}
          className="h-6 w-24 rounded-full"
          aria-hidden="true"
        />
      ))}
      <Skeleton className="h-8 w-20" aria-hidden="true" />
    </div>
  )
}

// Layout Skeleton with Sidebar and Content
export function LayoutSkeleton() {
  return (
    <div
      className="flex h-screen w-full bg-background"
      aria-label="Loading application layout"
      aria-busy="true"
    >
      {/* Sidebar Skeleton */}
      <div className="hidden md:flex w-64 flex-col  bg-ss-neutral-white">
        {/* Sidebar Header */}
        <div className="flex h-16 items-center gap-2 px-4 ">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-6 w-24" />
        </div>

        {/* Navigation Menu */}
        <div className="flex-1 overflow-auto p-4">
          <div className="space-y-2">
            {[
              'dashboard',
              'events',
              'users',
              'analytics',
              'settings',
              'billing',
              'support',
              'logout',
            ].map(item => (
              <div key={item} className="flex items-center gap-3 p-2">
                <Skeleton className="h-5 w-5" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="p-4 ">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden bg-ss-neutral-red/20">
        {/* Page Content */}
        <div className="flex-1 overflow-auto border border-ss-neutral-50 rounded-2xl m-8 !bg-gray-100/50">
          <div className="p-4 lg:p-6 space-y-6">
            {/* Page Title */}
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>

            {/* Cards Section */}
            <SectionCardsSkeleton />

            {/* Table Section */}
            <div className="space-y-4">
              <FilterBarSkeleton />
              <TableSkeleton />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Combined Loading Skeleton for the entire page
export function PageLoadingSkeleton() {
  return (
    <div
      className="w-full h-full"
      aria-label="Loading page content"
      aria-busy="true"
    >
      <SectionCardsSkeleton />
      <div className="mt-6">
        <FilterBarSkeleton />
        <TableSkeleton />
      </div>
    </div>
  )
}
